import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Plus, ClipboardList, Loader2, AlertCircle, X, Sparkles, School,
  CalendarDays, User, ChevronRight, Link2, Radar, RefreshCw, GraduationCap, Settings, LogOut, Filter
} from 'lucide-react';
import { getIncidentMeta, getIncidents, createIncident, updateIncidentStatus, getIncidentAnalytics, detectIncidentPatterns, formatApiError } from '../services/api';
import { CATEGORY_LABELS } from '../components/ActionTracker';

const SEVERITY = {
  low: 'bg-slate-100 text-slate-700', medium: 'bg-amber-100 text-amber-800',
  high: 'bg-orange-100 text-orange-800', critical: 'bg-red-600 text-white',
};
const STATUS = {
  open: { label: 'Open', cls: 'bg-red-50 text-red-700 border-red-200' },
  under_review: { label: 'Under Review', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  resolved: { label: 'Resolved', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};
const inputCls = 'w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

function LogIncidentModal({ meta, onClose, onCreated }) {
  const [form, setForm] = useState({ title: '', category: meta.categories[0], severity: 'medium', description: '', campus_id: '', occurred_at: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await createIncident({ ...form, campus_id: form.campus_id || null });
      onCreated(res.data.incident);
      onClose();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-lg animate-in max-h-[92vh] overflow-y-auto" data-testid="log-incident-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-outfit text-lg font-bold text-slate-900">Log an Incident</h2>
          <button type="button" onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm" data-testid="incident-error">{error}</div>}
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Short title (e.g. Student injured on playground)" className={inputCls} data-testid="incident-title-input" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="incident-category-select">
              {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className={inputCls} data-testid="incident-severity-select">
              {meta.severities.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)} severity</option>)}
            </select>
            <select value={form.campus_id} onChange={(e) => setForm({ ...form, campus_id: e.target.value })} className={inputCls} data-testid="incident-campus-select">
              <option value="">District-wide / not campus-specific</option>
              {meta.campuses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input type="date" value={form.occurred_at} onChange={(e) => setForm({ ...form, occurred_at: e.target.value })} className={inputCls} data-testid="incident-date-input" />
          </div>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required rows={5}
            placeholder="What happened, who was involved (no student names needed), what was the immediate response, and what may have contributed?" className={inputCls} data-testid="incident-description-input" />
          <p className="text-xs text-slate-400 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI will link this incident to the scorecard controls most likely involved.</p>
          <button type="submit" disabled={saving} className="w-full py-2.5 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="incident-submit">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Logging & analyzing...</> : <><Plus className="w-4 h-4" /> Log Incident</>}
          </button>
        </div>
      </form>
    </div>
  );
}

function IncidentCard({ incident, canManage, onStatusChange }) {
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const st = STATUS[incident.status] || STATUS.open;

  const change = async (status) => {
    setUpdating(true);
    try {
      await updateIncidentStatus(incident._id, { status });
      onStatusChange(incident._id, status);
    } catch (err) {
      alert(formatApiError(err));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-sm transition-all" data-testid={`incident-${incident._id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${SEVERITY[incident.severity]}`}>{incident.severity}</span>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{incident.category}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${st.cls}`} data-testid={`incident-status-${incident._id}`}>{st.label}</span>
          </div>
          <h4 className="font-semibold text-slate-900">{incident.title}</h4>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
            <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{incident.occurred_at}</span>
            <span className="flex items-center gap-1"><School className="w-3 h-3" />{incident.campus_name || 'District-wide'}</span>
            <span className="flex items-center gap-1"><User className="w-3 h-3" />{incident.reporter_name}</span>
          </div>
        </div>
        <button onClick={() => setOpen(!open)} className="text-sm text-blue-600 font-medium flex-shrink-0" data-testid={`incident-toggle-${incident._id}`}>{open ? 'Less' : 'Details'}</button>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
          <p className="text-sm text-slate-600 leading-relaxed">{incident.description}</p>
          {incident.linked_categories?.length > 0 && (
            <div className="bg-violet-50 border border-violet-100 rounded-lg p-3">
              <p className="text-xs font-semibold text-violet-800 flex items-center gap-1 mb-1.5"><Link2 className="w-3 h-3" /> Linked scorecard controls</p>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {incident.linked_categories.map((c) => <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-white border border-violet-200 text-violet-800">{CATEGORY_LABELS[c] || c}</span>)}
              </div>
              {incident.insight && <p className="text-sm text-violet-900">{incident.insight}</p>}
              {incident.suggested_action && <p className="text-sm text-violet-800 mt-1.5 flex items-start gap-1"><ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" /><span><b>Suggested:</b> {incident.suggested_action}</span></p>}
            </div>
          )}
          {canManage && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">Set status:</span>
              {Object.entries(STATUS).map(([k, v]) => (
                <button key={k} disabled={updating || incident.status === k} onClick={() => change(k)}
                  className={`px-3 py-1 rounded-full border text-xs font-medium disabled:opacity-40 ${incident.status === k ? v.cls : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-testid={`set-status-${k}-${incident._id}`}>
                  {v.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PatternsPanel({ analytics, onUpdated }) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const p = analytics?.patterns;

  const run = async () => {
    setRunning(true);
    setError('');
    try {
      const res = await detectIncidentPatterns();
      onUpdated(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="bg-slate-900 text-white rounded-2xl p-6" data-testid="patterns-panel">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><Radar className="w-5 h-5 text-violet-300" /></div>
          <div>
            <h3 className="font-outfit text-lg font-semibold">AI Pattern Detection</h3>
            <p className="text-sm text-slate-400">{p ? `Last run ${new Date(p.generated_at).toLocaleString()}` : 'Connect incidents to the scorecard weaknesses behind them.'}</p>
          </div>
        </div>
        <button onClick={run} disabled={running || !analytics?.total} className="px-4 py-2.5 bg-violet-400 text-slate-900 rounded-lg font-semibold text-sm hover:bg-violet-300 disabled:opacity-50 flex items-center gap-2" data-testid="run-patterns-btn">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : p ? <RefreshCw className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
          {running ? 'Analyzing...' : p ? 'Re-run' : 'Detect Patterns'}
        </button>
      </div>
      {error && <p className="text-sm text-red-300 mb-3">{error}</p>}
      {analytics?.linked_categories?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {analytics.linked_categories.slice(0, 6).map((l) => (
            <span key={l.key} className="text-xs bg-white/10 rounded-full px-3 py-1" data-testid={`linked-${l.key}`}>
              {l.name}: <b>{l.incidents}</b>{l.scorecard_pct != null && <span className={l.scorecard_pct < 50 ? 'text-red-300' : 'text-slate-300'}> · scorecard {l.scorecard_pct}%</span>}
            </span>
          ))}
        </div>
      )}
      {p && (
        <div className="space-y-3">
          <p className="text-slate-200" data-testid="patterns-summary">{p.summary}</p>
          {p.patterns?.map((pt, i) => (
            <div key={i} className="bg-white/5 rounded-lg px-4 py-3 text-sm">
              <p className="font-semibold">{pt.title}{pt.linked_category && <span className="ml-2 text-xs text-violet-300 font-normal">{CATEGORY_LABELS[pt.linked_category] || pt.linked_category}</span>}</p>
              <p className="text-slate-300 mt-0.5">{pt.detail}</p>
            </div>
          ))}
          {p.recommendations?.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Recommended prevention</p>
              {p.recommendations.map((r, i) => <p key={i} className="text-sm text-slate-200 flex items-start gap-2"><ChevronRight className="w-4 h-4 text-violet-300 mt-0.5 flex-shrink-0" />{r}</p>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function IncidentsPage() {
  const { user, logout } = useAuth();
  const isStaff = user?.role === 'staff';
  const canManage = user?.role === 'admin' || user?.role === 'district_admin';
  const [meta, setMeta] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLog, setShowLog] = useState(false);
  const [filters, setFilters] = useState({ status: '', category: '', campus_id: '' });

  const load = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [m, list, an] = await Promise.all([
        getIncidentMeta(), getIncidents(params),
        isStaff ? Promise.resolve({ data: null }) : getIncidentAnalytics().catch(() => ({ data: null })),
      ]);
      setMeta(m.data);
      setIncidents(list.data.incidents || []);
      setAnalytics(an.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters, isStaff]);

  useEffect(() => { load(); }, [load]);

  const handleCreated = (incident) => {
    setIncidents((prev) => [incident, ...prev]);
    if (!isStaff) getIncidentAnalytics().then((r) => setAnalytics(r.data)).catch(() => {});
  };

  const handleStatusChange = (id, status) => {
    setIncidents((prev) => prev.map((i) => (i._id === id ? { ...i, status } : i)));
    if (!isStaff) getIncidentAnalytics().then((r) => setAnalytics(r.data)).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            {!isStaff && <Link to="/dashboard" className="p-2 hover:bg-slate-100 rounded-lg transition-all"><ArrowLeft className="w-5 h-5 text-slate-500" /></Link>}
            <div className="min-w-0">
              <h1 className="font-outfit text-xl font-bold text-slate-900">Incident Log</h1>
              <p className="text-sm text-slate-500 truncate">{user?.district_name || 'Your District'}{isStaff ? ` · ${user?.name}` : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isStaff && (
              <>
                <Link to="/training" className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg font-medium flex items-center gap-1.5" data-testid="staff-nav-training"><GraduationCap className="w-4 h-4" /> <span className="hidden sm:inline">Training</span></Link>
                <Link to="/settings" className="p-2 text-slate-500 hover:bg-slate-50 rounded-lg" data-testid="staff-nav-settings"><Settings className="w-4 h-4" /></Link>
                <button onClick={logout} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg" data-testid="staff-logout"><LogOut className="w-4 h-4" /></button>
              </>
            )}
            <button onClick={() => setShowLog(true)} disabled={!meta} className="px-4 py-2.5 bg-slate-900 text-white rounded-lg font-medium text-sm hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2" data-testid="log-incident-btn">
              <Plus className="w-4 h-4" /> Log Incident
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {analytics && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="incident-stats">
              {[
                ['Total incidents', analytics.total, 'text-slate-900'],
                ['Open', analytics.open, analytics.open ? 'text-red-600' : 'text-slate-900'],
                ['Last 30 days', analytics.last_30_days, 'text-blue-600'],
                ['Resolution rate', `${analytics.resolution_rate}%`, 'text-emerald-600'],
              ].map(([label, val, cls]) => (
                <div key={label} className="bg-white border border-slate-200 rounded-xl p-5">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</p>
                  <p className={`text-3xl font-outfit font-bold ${cls}`}>{val}</p>
                </div>
              ))}
            </div>

            {(analytics.by_category.length > 0 || analytics.by_campus.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <h3 className="font-outfit font-semibold text-slate-900 mb-3">By category</h3>
                  {analytics.by_category.map((c) => (
                    <div key={c.name} className="flex items-center gap-3 text-sm mb-2">
                      <span className="w-40 text-slate-600 truncate">{c.name}</span>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-red-400 rounded-full" style={{ width: `${(c.count / analytics.total) * 100}%` }} /></div>
                      <span className="w-6 text-right font-medium text-slate-900">{c.count}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <h3 className="font-outfit font-semibold text-slate-900 mb-3">By campus</h3>
                  {analytics.by_campus.map((c) => (
                    <div key={c.name} className="flex items-center gap-3 text-sm mb-2">
                      <span className="w-40 text-slate-600 truncate">{c.name}</span>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-blue-400 rounded-full" style={{ width: `${(c.count / analytics.total) * 100}%` }} /></div>
                      <span className="w-6 text-right font-medium text-slate-900">{c.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <PatternsPanel analytics={analytics} onUpdated={(p) => setAnalytics((prev) => ({ ...prev, patterns: p }))} />
          </>
        )}

        {isStaff && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 text-sm text-blue-900 flex items-start gap-3">
            <ClipboardList className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <p>Log anything that affected safety, security, health, or operations at your school. District leadership reviews every incident, and AI connects each one to the district's risk controls.</p>
          </div>
        )}

        <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <h3 className="font-outfit text-lg font-semibold text-slate-900">{isStaff ? 'My incidents' : 'All incidents'} ({incidents.length})</h3>
            {meta && (
              <div className="flex items-center gap-2 text-sm" data-testid="incident-filters">
                <Filter className="w-4 h-4 text-slate-400" />
                <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="px-2 py-1.5 border border-slate-200 rounded-lg text-xs" data-testid="filter-status">
                  <option value="">All statuses</option>{meta.statuses.map((s) => <option key={s} value={s}>{STATUS[s].label}</option>)}
                </select>
                <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className="px-2 py-1.5 border border-slate-200 rounded-lg text-xs" data-testid="filter-category">
                  <option value="">All categories</option>{meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {meta.campuses.length > 0 && (
                  <select value={filters.campus_id} onChange={(e) => setFilters({ ...filters, campus_id: e.target.value })} className="px-2 py-1.5 border border-slate-200 rounded-lg text-xs" data-testid="filter-campus">
                    <option value="">All campuses</option>{meta.campuses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                )}
              </div>
            )}
          </div>

          {loading ? (
            <p className="text-slate-400 animate-pulse">Loading incidents...</p>
          ) : incidents.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center" data-testid="incidents-empty">
              <ClipboardList className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-600 font-medium">No incidents logged{Object.values(filters).some(Boolean) ? ' for these filters' : ' yet'}.</p>
              <p className="text-sm text-slate-400 mt-1">Every logged incident makes your scorecard, roadmap, and evidence pack smarter.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="incident-list">
              {incidents.map((i) => <IncidentCard key={i._id} incident={i} canManage={canManage} onStatusChange={handleStatusChange} />)}
            </div>
          )}
        </div>
      </main>

      {showLog && meta && <LogIncidentModal meta={meta} onClose={() => setShowLog(false)} onCreated={handleCreated} />}
    </div>
  );
}
